import React, { useRef, useState } from 'react'
import {
  CCard,
  CCardBody,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import Notify from '../Notify'

const API_URL = process.env.REACT_APP_API_URL
const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_LOGO_TYPES = 'image/jpeg,image/png,image/gif'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(50, '50 Character Limit is allowed.'),
  url: Yup.string().required('Website URL is required'),
  image: Yup.mixed()
    .nullable(true)
    .test(
      'fileSize',
      'File size too large, max file size is 10Mb or resolution is not 500px x 500px',
      (file) => (file ? file.size <= 500 * 500 : true),
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || (value && SUPPORTED_FORMATS.includes(value?.type)),
    ),
})

const BrandForm = ({ brandingDetail, loading, showPosBased, onSaved, onDeleted }) => {
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  const formik = useFormik({
    initialValues: {
      name: brandingDetail?.name || '',
      url: brandingDetail?.url || '',
      facebook: brandingDetail?.facebook || '',
      twitter: brandingDetail?.twitter || '',
      instagram: brandingDetail?.instagram || '',
      about: brandingDetail?.about || '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('url', data.url)
      formData.append('image', data.image)
      formData.append('facebook', data.facebook)
      formData.append('twitter', data.twitter)
      formData.append('instagram', data.instagram)
      formData.append('about', data.about)
      formData.append('is_show', showPosBased)

      setSaving(true)
      CommonService.editBranding(formData)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            onSaved?.()
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setSaving(false))
    },
  })

  const clearBranding = () => {
    setDeleting(true)
    CommonService.deleteBranding()
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.data.message, 'success')
          formik.resetForm()
          onDeleted?.()
        }
      })
      .catch(() => {
        ToastComponent('Something went wrong. Please try again.', 'error')
      })
      .finally(() => setDeleting(false))
  }

  return (
    <CCard className="br-panel mb-3">
      <CCardBody>
        <div className="br-panel-toolbar">
          <div>
            <h2 className="br-panel-title">Club Details</h2>
            <p className="br-panel-note">
              Shown wherever members see your club&apos;s branding in the app.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading branding details...</span>
          </div>
        ) : (
          <>
            <CForm className="row g-3 align-content-start" onSubmit={formik.handleSubmit}>
              <CCol md={6}>
                <CFormLabel htmlFor="name">Name *</CFormLabel>
                <CFormInput
                  id="name"
                  name="name"
                  placeholder="Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.errors.name && formik.touched.name)}
                />
                {formik.errors.name && formik.touched.name && (
                  <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
                )}
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="url">Website *</CFormLabel>
                <CFormInput
                  id="url"
                  name="url"
                  placeholder="Website"
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.errors.url && formik.touched.url)}
                />
                {formik.errors.url && formik.touched.url && (
                  <CFormFeedback invalid>{formik.errors.url}</CFormFeedback>
                )}
              </CCol>

              <CCol xs={12}>
                <hr className="mt-1 mb-0" />
                <p className="br-section-title mt-3 mb-0">Social Links</p>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="facebook">Facebook URL</CFormLabel>
                <CFormInput
                  id="facebook"
                  name="facebook"
                  placeholder="Facebook URL"
                  value={formik.values.facebook}
                  onChange={formik.handleChange}
                  aria-label="facebook"
                />
                <small className="br-hint">Ex: https://www.facebook.com/myclubtap</small>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="twitter">Twitter URL</CFormLabel>
                <CFormInput
                  id="twitter"
                  name="twitter"
                  placeholder="Twitter URL"
                  value={formik.values.twitter}
                  onChange={formik.handleChange}
                  aria-label="twitter"
                />
                <small className="br-hint">Ex: https://www.twitter.com/myclubtap</small>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="instagram">Instagram URL</CFormLabel>
                <CFormInput
                  id="instagram"
                  name="instagram"
                  placeholder="Instagram URL"
                  value={formik.values.instagram}
                  onChange={formik.handleChange}
                  aria-label="instagram"
                />
                <small className="br-hint">Ex: https://www.instagram.com/myclubtap</small>
              </CCol>

              <CCol xs={12}>
                <hr className="mt-1 mb-0" />
                <p className="br-section-title mt-3 mb-0">Logo &amp; About</p>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="image">Brand Logo</CFormLabel>
                <ImageDropzone
                  accept={ACCEPTED_LOGO_TYPES}
                  hint="JPG, PNG or GIF. 500px x 500px recommended."
                  minHeight={150}
                  file={formik.values.image}
                  existingUrl={
                    brandingDetail?.logo
                      ? `${API_URL}uploads/branding/${brandingDetail.logo}`
                      : null
                  }
                  error={formik.touched.image ? formik.errors.image : null}
                  onChange={(selected) => {
                    formik.setTouched({ ...formik.touched, image: true })
                    formik.setFieldValue('image', selected)
                  }}
                />
              </CCol>
              <CCol md={8}>
                <CFormLabel htmlFor="about">About Brand</CFormLabel>
                <div className="br-editor">
                  <ReactQuill
                    theme="snow"
                    ref={quillRef}
                    value={formik.values.about}
                    onChange={(content) => formik.setFieldValue('about', content)}
                    modules={modules}
                  />
                </div>
              </CCol>

              <CCol xs={12}>
                <div className="br-form-actions">
                  <CLoadingButton type="submit" color="primary" loading={saving}>
                    Save Changes
                  </CLoadingButton>
                  <CLoadingButton
                    type="button"
                    color="danger"
                    variant="outline"
                    loading={deleting}
                    onClick={() => setShowConfirm(true)}
                  >
                    Delete Branding
                  </CLoadingButton>
                </div>
              </CCol>
            </CForm>

            <Notify
              showConfirm={showConfirm}
              handleCancel={() => setShowConfirm(false)}
              handleConfirm={() => {
                setShowConfirm(false)
                clearBranding()
              }}
              text="Are you sure you want to delete your branding details? This removes the logo, links and about copy."
            />
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

export default BrandForm
